<app-sidebar></app-sidebar>

<main class="settings-main">
  <div class="settings-content">

    <!-- Page Header -->
    <div class="page-header">
      <div class="page-header-left">
        <div class="page-icon">
          <i data-lucide="settings" class="header-icon"></i>
        </div>
        <div>
          <h1 class="page-title">Settings</h1>
          <p class="page-subtitle">Manage your account preferences and security</p>
        </div>
      </div>
      <div class="user-badge">
        <i data-lucide="user-circle" class="badge-icon"></i>
        <div>
          <div class="badge-email">{{ userEmail }}</div>
          <div class="badge-role">{{ userRole | titlecase }}</div>
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <div class="settings-tabs">
      <button class="tab-btn" [class.active]="activeTab === 'security'" (click)="activeTab = 'security'">
        <i data-lucide="shield" class="tab-icon"></i>
        Security
      </button>
    </div>

    <!-- Security Tab -->
    <div class="settings-card" *ngIf="activeTab === 'security'">
      <div class="card-header-section">
        <div class="card-icon-wrap">
          <i data-lucide="lock" class="card-icon"></i>
        </div>
        <div>
          <h2 class="card-title">Change Password</h2>
          <p class="card-subtitle">Keep your account secure by updating your password regularly</p>
        </div>
      </div>

      <form [formGroup]="passwordForm" (ngSubmit)="onChangePassword()" class="password-form">

        <!-- Current Password -->
        <div class="form-group">
          <label class="form-label" for="currentPassword">Current Password</label>
          <div class="input-wrapper">
            <i data-lucide="lock" class="input-icon"></i>
            <input id="currentPassword" [type]="showCurrentPassword ? 'text' : 'password'" class="form-input"
              formControlName="currentPassword" placeholder="Enter current password"
              [class.has-error]="passwordForm.get('currentPassword')?.touched && passwordForm.get('currentPassword')?.invalid" />
            <button type="button" class="toggle-eye" (click)="showCurrentPassword = !showCurrentPassword" tabindex="-1">
              <i [attr.data-lucide]="showCurrentPassword ? 'eye-off' : 'eye'" class="eye-icon"></i>
            </button>
          </div>
          <div class="field-error" *ngIf="getFieldError('currentPassword')">
            <i data-lucide="alert-circle" class="err-icon"></i>
            {{ getFieldError('currentPassword') }}
          </div>
        </div>

        <!-- Divider -->
        <div class="form-divider"></div>

        <!-- New Password -->
        <div class="form-group">
          <label class="form-label" for="newPassword">New Password</label>
          <div class="input-wrapper">
            <i data-lucide="key" class="input-icon"></i>
            <input id="newPassword" [type]="showNewPassword ? 'text' : 'password'" class="form-input"
              formControlName="newPassword" placeholder="Enter new password"
              [class.has-error]="passwordForm.get('newPassword')?.touched && passwordForm.get('newPassword')?.invalid" />
            <button type="button" class="toggle-eye" (click)="showNewPassword = !showNewPassword" tabindex="-1">
              <i [attr.data-lucide]="showNewPassword ? 'eye-off' : 'eye'" class="eye-icon"></i>
            </button>
          </div>
          <div class="field-error" *ngIf="getFieldError('newPassword')">
            <i data-lucide="alert-circle" class="err-icon"></i>
            {{ getFieldError('newPassword') }}
          </div>

          <!-- Password Strength -->
          <div class="strength-container" *ngIf="passwordForm.get('newPassword')?.value">
            <div class="strength-bar-track">
              <div class="strength-bar-fill" [class]="passwordStrengthClass" [style.width.%]="passwordStrength"></div>
            </div>
            <span class="strength-label" [class]="passwordStrengthClass">{{ passwordStrengthLabel }}</span>
          </div>
          <div class="password-hints">
            <div class="hint" [class.met]="passwordForm.get('newPassword')?.value?.length >= 8">
              <i [attr.data-lucide]="passwordForm.get('newPassword')?.value?.length >= 8 ? 'check-circle' : 'circle'"
                class="hint-icon"></i>
              At least 8 characters
            </div>
            <div class="hint" [class.met]="hasUppercase(passwordForm.get('newPassword')?.value)">
              <i [attr.data-lucide]="hasUppercase(passwordForm.get('newPassword')?.value) ? 'check-circle' : 'circle'"
                class="hint-icon"></i>
              One uppercase letter
            </div>
            <div class="hint" [class.met]="hasNumber(passwordForm.get('newPassword')?.value)">
              <i [attr.data-lucide]="hasNumber(passwordForm.get('newPassword')?.value) ? 'check-circle' : 'circle'"
                class="hint-icon"></i>
              One number
            </div>
            <div class="hint" [class.met]="hasSpecialChar(passwordForm.get('newPassword')?.value)">
              <i [attr.data-lucide]="hasSpecialChar(passwordForm.get('newPassword')?.value) ? 'check-circle' : 'circle'"
                class="hint-icon"></i>
              One special character (&#64;$!%*?&)
            </div>
          </div>
        </div>

        <!-- Confirm Password -->
        <div class="form-group">
          <label class="form-label" for="confirmPassword">Confirm New Password</label>
          <div class="input-wrapper">
            <i data-lucide="key" class="input-icon"></i>
            <input id="confirmPassword" [type]="showConfirmPassword ? 'text' : 'password'" class="form-input"
              formControlName="confirmPassword" placeholder="Confirm new password"
              [class.has-error]="passwordForm.get('confirmPassword')?.touched && passwordForm.get('confirmPassword')?.invalid" />
            <button type="button" class="toggle-eye" (click)="showConfirmPassword = !showConfirmPassword" tabindex="-1">
              <i [attr.data-lucide]="showConfirmPassword ? 'eye-off' : 'eye'" class="eye-icon"></i>
            </button>
          </div>
          <div class="field-error" *ngIf="getFieldError('confirmPassword')">
            <i data-lucide="alert-circle" class="err-icon"></i>
            {{ getFieldError('confirmPassword') }}
          </div>
          <div class="match-success"
            *ngIf="passwordForm.get('confirmPassword')?.touched && passwordForm.get('confirmPassword')?.valid && passwordForm.get('confirmPassword')?.value">
            <i data-lucide="check-circle" class="success-icon"></i>
            Passwords match
          </div>
        </div>

        <!-- Submit -->
        <div class="form-actions">
          <button type="button" class="btn-secondary"
            (click)="passwordForm.reset(); passwordStrength = 0; passwordStrengthLabel = ''; passwordStrengthClass = ''"
            [disabled]="isSubmitting">
            Cancel
          </button>
          <button type="submit" class="btn-primary" [disabled]="isSubmitting || passwordForm.invalid"
            id="changePasswordSubmit">
            <span *ngIf="!isSubmitting">
              Update Password
            </span>
            <span *ngIf="isSubmitting" class="loading-spinner">
              <span class="spinner"></span>
              Updating...
            </span>
          </button>
        </div>
      </form>
    </div>

  </div>
</main>